import { injectable } from 'inversify';
import container from '@core/di';
import BaseStore from '@core/stores/base';
import { Profile, SocialAuthProvider } from '@shared/models/profile';
import { makeObservable, observable, action, runInAction } from 'mobx';
import ProfileService from '@shared/services/profile';

@injectable()
export default class ProfileStore extends BaseStore<Profile, ProfileService> {
  static diToken = Symbol('profile-store');

  private _profile: Profile = undefined as unknown as Profile;
  private _profileFetching: boolean = false;

  constructor() {
    super({ service: container.get<ProfileService>(ProfileService.diToken) });
    makeObservable<any>(this, {
      _profile: observable,
      _profileFetching: observable,
      current: action,
      unlinkProvider: action,
    });
  }

  get profile(): Profile {
    return this._profile;
  }

  get profileFetching(): boolean {
    return this._profileFetching;
  }

  set profile(profile: Profile) {
    this._profile = profile;
  }

  current = async () => {
    try {
      runInAction(() => {
        this._profileFetching = true;
      });

      const profile = await this.service.getProfile();
      profile.linkedProviders = await this.service.getLinkedProviders();

      runInAction(() => {
        this._profile = profile;
      });

      return profile;
    } finally {
      runInAction(() => {
        this._profileFetching = false;
      });
    }
  };

  update = async data => {
    return this.service.update(data);
  };

  removeAvatar = async () => {
    return this.service.removeAvatar();
  };

  removeUser = async (ssoId: string) => {
    return this.service.removeUser(ssoId);
  };

  uploadAvatar = async formData => {
    return this.service.uploadAvatar(formData);
  };

  unlinkProvider = async (provider: SocialAuthProvider) => {
    await this.service.unlinkProvider(provider);

    const updatedLinkedProviders = await this.service.getLinkedProviders();

    runInAction(() => {
      if (this._profile) {
        this._profile.linkedProviders = updatedLinkedProviders;
      }
    });
  };

  changePassword = async (oldPassword: string, newPassword: string) => {
    return await this.service.changePassword(oldPassword, newPassword);
  };

  sendChangeEmailCode = async (newEmail: string) => {
    return await this.service.sendChangeEmailCode(newEmail);
  };

  changeEmailWithCode = async (newEmail: string, activationCode: string) => {
    return await this.service.changeEmailWithCode(newEmail, activationCode);
  };
}
